import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-card/40 px-8 py-16 text-center">
      {Icon && <Icon className="h-8 w-8 text-muted-foreground" />}
      <div className="text-sm font-medium">{title}</div>
      {description && <div className="max-w-md text-xs text-muted-foreground">{description}</div>}
      {action}
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string };
    const parts = [
      e.message,
      e.details,
      e.hint ? `hint: ${e.hint}` : undefined,
      e.code ? `(${e.code})` : undefined,
    ].filter(Boolean);
    if (parts.length) return parts.join(' — ');
  }
  return String(error);
}

export function ErrorState({ error }: { error: unknown }) {
  const message = errorMessage(error);
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
      <div className="font-medium">Something went wrong</div>
      <div className="mt-1 break-all font-mono text-xs opacity-80">{message}</div>
    </div>
  );
}
